/**
 * Business Domain Selector with search functionality
 *
 * Features:
 * - Type to search existing domains
 * - Shows domain color badges
 * - Single select (domains are mutually exclusive)
 */

import { useState, useCallback, useMemo } from 'react';
import { Select, Spin, Space } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { businessDomainsApi } from '@api/index';

interface BusinessDomainSelectorProps {
  value?: string | string[];
  onChange?: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  mode?: 'multiple';
}

export default function BusinessDomainSelector({
  value,
  onChange,
  placeholder = 'Select domain...',
  disabled = false,
  size,
  mode,
}: BusinessDomainSelectorProps) {
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['business-domains'],
    queryFn: () => businessDomainsApi.list({ includeHidden: false }),
  });

  const filteredDomains = useMemo(() => {
    if (!searchValue) return domains;
    const lower = searchValue.toLowerCase();
    return domains.filter((domain) => domain.name.toLowerCase().includes(lower));
  }, [domains, searchValue]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleSelect = useCallback(
    (selectedValue: any) => {
      onChange?.(selectedValue);
      if (mode !== 'multiple') {
        setSearchValue('');
        setOpen(false);
      }
    },
    [onChange, mode]
  );

  const handleClear = useCallback(() => {
    onChange?.(undefined);
    setSearchValue('');
  }, [onChange]);

  const options = useMemo(() => {
    return filteredDomains.map((domain) => ({
      value: domain.id,
      label: (
        <Space>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: domain.color,
            }}
          />
          {domain.name}
        </Space>
      ),
      domain,
    }));
  }, [filteredDomains]);

  return (
    <Select
      showSearch
      allowClear
      mode={mode}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoading}
      filterOption={false}
      searchValue={searchValue}
      onSearch={handleSearch}
      onChange={handleSelect}
      onClear={handleClear}
      open={open}
      onDropdownVisibleChange={setOpen}
      options={options}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      size={size}
      suffixIcon={<AppstoreOutlined />}
      optionRender={(option) => option.label}
      menuItemSelectedIcon={
        mode === 'multiple' ? (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#52c41a',
            }}
          />
        ) : undefined
      }
      autoClearSearchValue={mode !== 'multiple'}
      maxTagCount="responsive"
    />
  );
}
